import { Moment } from 'moment/moment';

import { DayHours, Exception } from '../models';
import { timeToTimestamp } from './time-to-timestamp';
import { Interval, Intervals } from '../types';
import { getStartAndEndOfDay } from './get-start-and-end-of-the-day';

/**
 * Generates working intervals from schedule for given date and with given offset by
 * the following priority:
 * - any closed exception - empty intervals, closed the whole day
 * - any other exceptions - returns interval (since time to time) for these exceptions
 * - regular schedule - return interval according regular schedule
 *
 * Examples:
 * closed: []
 * exception 8AM-11PM: [28800000, 82800000]
 * regular schedule 9AM-1PM and 2PM-7PM: [[36000000, 46800000], [50400000, 68400000]]
 * regular schedule 9AM-6PM: [[36000000, 64800000]]
 *
 * @param {object} dayHours Regular hours for any weekday
 * @param {array} exceptions Schedule exceptions
 * @param {Moment} date date to calculate start and end of the day if schedule is empty
 * @param {string} timezone Is added start and end of intervals
 * @param {boolean} isPublic
 * @returns {[]|*[]} array of intervals
 */
export function generateIntervals(
  dayHours: DayHours,
  exceptions: Exception[],
  date: Moment,
  timezone: string,
  isPublic = false,
): Intervals {
  if (isPublic) {
    return generateIntervalsPublic(dayHours, exceptions, date, timezone, true);
  }

  const dateString = date.format('D-M-YYYY');
  const intervals: Intervals = [];
  for (const exception of exceptions) {
    if (exception.closed) {
      return [];
    }

    intervals.push(...exception.hours.map(
      hours => hours.map(
        (hour, index) => timeToTimestamp(Number(hour), dateString, timezone, exception.nextDay && index > 0)
      ) as Interval
    ));
  }

  // No exceptions for this day
  if (intervals.length) {
    return intervals;
  }

  if (dayHours.closed) {
    return [];
  }

  const startOfDay = date.clone().startOf('day');
  const startOfDayTimestamp = startOfDay.valueOf();
  const endOfDayTimestamp = startOfDay.clone().add(1, 'day').valueOf();
  intervals.push(...dayHours.hours.map(hours => hours.map((hour, index) => {
    if (hour === '') {
      return index === 0 ? startOfDayTimestamp : endOfDayTimestamp;
    }

    return timeToTimestamp(Number(hour), dateString, timezone, dayHours.nextDay && index > 0);
  }) as Interval));

  return intervals;
}

/**
 * Generates working intervals from schedule for given date and with given offset by
 * the following priority:
 * - any closed exception - empty intervals, closed the whole day
 * - any other exceptions - returns interval (since time to time) for these exceptions
 * - regular schedule - return interval according regular schedule
 *
 * Examples:
 * closed: []
 * exception 8AM-11PM: [28800000, 82800000]
 * regular schedule 9AM-1PM and 2PM-7PM: [[36000000, 46800000], [50400000, 68400000]]
 * regular schedule 9AM-6PM: [[36000000, 64800000]]
 *
 * @param {DayHours} dayHours Regular hours for any weekday
 * @param {array} exceptions Schedule exceptions
 * @param {Moment} date
 * @param {string} timezone Is added start and end of intervals
 * @param {boolean} isPublic
 * @returns {[]|*[]} array of intervals
 */
// TODO: understand how this piece of shit works and try to merge functionality with function above
function generateIntervalsPublic(
  dayHours: DayHours,
  exceptions: Exception[],
  date: Moment,
  timezone: string,
  isPublic = false,
): Intervals {
  const dateString = date.format('D-M-YYYY');
  const intervals: Intervals = [];

  for (const exception of exceptions) {
    if ((exception.hours[0][0] === '' && exception.publicDisabled) || (isPublic && exception.publicDisabled)) {
      continue;
    }

    intervals.push(...exception.hours.map(hours => hours.map((hour, index) => {
      const addDay = exception.nextDay && index > 0;

      return timeToTimestamp(Number(hour), dateString, timezone, addDay);
    }) as Interval));
  }

  // No exceptions for this day
  if (intervals.length) {
    return intervals;
  }

  let closedException = null;
  let openedException = null;
  let publicDisabledException = null;

  for (const exception of exceptions) {
    if (exception.closed) {
      closedException = exception;
    }

    if (!exception.closed) {
      openedException = exception;
    }

    if (isPublic && exception.publicDisabled) {
      publicDisabledException = exception;
    }
  }

  const isClosed = (dayHours.closed || !!closedException) && !openedException;
  const publicDisabled = isPublic && (dayHours.publicDisabled || !!publicDisabledException);
  if (isClosed || publicDisabled) {
    return [];
  }

  const [startOfDayTimestamp, endOfDayTimestamp] = getStartAndEndOfDay(date);
  intervals.push(...dayHours.hours.map(hours => hours.map((hour, index) => {
    if (hour === '') {
      return index === 0 ? startOfDayTimestamp : endOfDayTimestamp;
    }

    return timeToTimestamp(Number(hour), dateString, timezone, dayHours.nextDay && index > 0);
  }) as Interval));

  return intervals;
}
